"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import { Lightbulb } from "lucide-react";
import { saveLightParams, supbaseManager, getUserId } from "../API";

type ToneMappingKey =
  | "NoToneMapping"
  | "NeutralToneMapping"
  | "AgXToneMapping"
  | "LinearToneMapping"
  | "ReinhardToneMapping"
  | "CineonToneMapping"
  | "ACESFilmicToneMapping";

type LightParams = {
  toneMapping: ToneMappingKey;
  exposure: number;

  fov: number; // 👈 NOVO

  ambientEnabled: boolean;
  ambientIntensity: number;
  ambientColor: string; // hex

  envMapIntensity: number;
  envRotationY: number; // radians

  hdriEnabled: boolean;
  hdrPresetUrl: string; // sempre preset
};


const Light = () => {
  const canvasRef = useRef<HTMLDivElement | null>(null);

  // presets HDR (adicione quantos quiser)
  const hdrPresets = useMemo(
    () => [
      { label: "Neutral", url: "/hdr/neutral.hdr" },
      { label: "Studio Small", url: "/hdr/studio_small_08_1k.hdr" },
      { label: "Aircraft Workshop", url: "/hdr/aircraft_workshop_01_1k.hdr" },
      { label: "White Studio", url: "/hdr/white_studio_03_1k.hdr" },
      { label: "Wooden Studio", url: "/hdr/wooden_studio_19_1k.hdr" },
      { label: "Studio Small_09_1k", url: "/hdr/studio_small_09_1k.hdr" },
      { label: "Pav Studio", url: "/hdr/pav_studio_01_1k.hdr" },
      { label: "Studio Small", url: "/hdr/studio_small_03_1k.hdr" },
      { label: "Abandoned Tank Farm", url: "/hdr/abandoned_tank_farm_01_1k.hdr" },
      { label: "Qwantani Puresky", url: "/hdr/qwantani_puresky_1k.hdr" },
      { label: "Lightroom", url: "/hdr/lightroom_14b.hdr" },
    ],
    []
  );

  const LIGHT_PRESETS: Record<string, Partial<LightParams>> = {
    neutral: {
      toneMapping: "NoToneMapping",
      exposure: 1,
      ambientEnabled: true,
      ambientIntensity: 0.75,
      ambientColor: "#ffffff",
      envMapIntensity: 0.4,
      envRotationY: 0,
      hdriEnabled: true,
      hdrPresetUrl: hdrPresets[0]?.url ?? "",
    },

    neutral2: {
      toneMapping: "NeutralToneMapping",
      exposure: 1,
      ambientEnabled: true,
      ambientIntensity: 2,
      ambientColor: "#ffffff",
      envMapIntensity: 1,
      envRotationY: 0.5,
      hdriEnabled: true,
      hdrPresetUrl: hdrPresets[0]?.url ?? "",
    },

    studio: {
      toneMapping: "ACESFilmicToneMapping",
      exposure: 1,
      ambientEnabled: true,
      ambientIntensity: 0.75,
      ambientColor: "#ffffff",
      envMapIntensity: 0.8,
      envRotationY: 2.22,
      hdriEnabled: true,
      hdrPresetUrl: hdrPresets[0]?.url ?? "",
    },

    soft: {
      toneMapping: "ACESFilmicToneMapping",
      exposure: 1,
      ambientEnabled: true,
      ambientIntensity: 1,
      ambientColor: "#ffffff",
      envMapIntensity: 0.25,
      envRotationY: 0,
      hdriEnabled: true,
      hdrPresetUrl: hdrPresets[0]?.url ?? "",
    },

    dramatic: {
      toneMapping: "ACESFilmicToneMapping",
      exposure: 0.9,
      ambientEnabled: true,
      ambientIntensity: 0.2,
      ambientColor: "#ffffff",
      envMapIntensity: 1.4,
      envRotationY: 1.2,
      hdriEnabled: true,
      hdrPresetUrl: hdrPresets[1]?.url ?? "",
    },
  };

  const DEFAULTS: LightParams = useMemo(
    () => ({
      toneMapping: "NoToneMapping",
      exposure: 1.0,

      fov: 50, // 👈 NOVO

      ambientEnabled: true,
      ambientIntensity: 0.75,
      ambientColor: "#ffffff",

      envMapIntensity: 0.4,
      envRotationY: 0,

      hdriEnabled: true,
      hdrPresetUrl: hdrPresets[0]?.url ?? "",
    }),
    [hdrPresets]
  );

  const [params, setParams] = useState<LightParams>(DEFAULTS);

  useEffect(() => {
    if (!canvasRef.current) return;
  }, []);

  const pushParams = (next: LightParams) => {
    setParams(next);
    const w = window as unknown as { [key: string]: any };
    if (w.integrationRef?.updateParams) w.integrationRef.updateParams(next);
  };

  const saveLight = (params: LightParams) => {
    saveLightParams(params);
  };

  function mergeLightParams(DEFAULTS: any, fromApi: any) {
    const merged = { ...DEFAULTS, ...(fromApi ?? {}) };

    // garante números
    merged.exposure = Number(merged.exposure ?? DEFAULTS.exposure);
    merged.ambientIntensity = Number(merged.ambientIntensity ?? DEFAULTS.ambientIntensity);
    merged.envMapIntensity = Number(merged.envMapIntensity ?? DEFAULTS.envMapIntensity);
    merged.envRotationY = Number(merged.envRotationY ?? DEFAULTS.envRotationY);

    // garante booleans
    merged.ambientEnabled = !!merged.ambientEnabled;
    merged.hdriEnabled = !!merged.hdriEnabled;

    // garante strings
    merged.ambientColor = merged.ambientColor ?? DEFAULTS.ambientColor;
    merged.hdrPresetUrl = merged.hdrPresetUrl ?? DEFAULTS.hdrPresetUrl;
    merged.toneMapping = merged.toneMapping ?? DEFAULTS.toneMapping;

    merged.fov = Number(merged.fov ?? DEFAULTS.fov);

    return merged;
  }

  async function loadLightParams() {

    const userId = getUserId() || (window as any).idClient;

    const lightParams = await supbaseManager(
      "get",
      "light",
      null,
      userId
    );

    if (lightParams?.[0]?.params) {

      const merged = {
        ...DEFAULTS,
        ...lightParams[0].params
      };

      setParams(merged);

      (window as any).integrationRef?.updateParams?.(merged);

    } else {

      setParams(DEFAULTS);
      (window as any).integrationRef?.updateParams?.(DEFAULTS);

    }

  }

  useEffect(() => {
    (window as any).loadLightParams = loadLightParams
  }, []);

  function applyPreset(name: keyof typeof LIGHT_PRESETS) {

    const preset = {
      ...DEFAULTS,
      ...LIGHT_PRESETS[name]
    };

    pushParams(preset);

  }

  return (
    <div ref={canvasRef} className="w-full max-w-md space-y-4 rounded-xl bg-card/85 p-3 sm:p-4">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 shrink-0">
          <Lightbulb className="h-5 w-5 text-primary" />
        </div>
        <div className="min-w-0">
          <h1 className="text-lg sm:text-xl font-semibold tracking-tight text-foreground truncate">
            Opções de Iluminação
          </h1>
          <p className="text-xs sm:text-sm text-muted-foreground">
            Ajuste o look da cena em tempo real
          </p>
        </div>
      </div>

      {/* Grid responsivo */}
      <div className="flex flex-col">

        {/* Presets */}
        <section className="rounded-xl border border-border/60 bg-background/40 p-3 sm:p-4 space-y-3 m-1">

          <div className="flex items-center justify-between">
            <h2 className="text-sm font-medium">Presets</h2>
            <span className="text-xs text-muted-foreground">
              Iluminação rápida
            </span>
          </div>

          <select
            className="w-full h-10 rounded-md border border-border bg-background px-3 text-sm"
            onChange={(e) => {
              if (e.target.value) applyPreset(e.target.value as keyof typeof LIGHT_PRESETS)
            }}
            defaultValue=""
          >

            <option value="" disabled>
              Selecionar preset
            </option>

            <option value="neutral">
              Neutral
            </option>

            <option value="neutral2">
              Neutral 2
            </option>

            <option value="studio">
              Studio
            </option>

            <option value="soft">
              Soft
            </option>

            <option value="dramatic">
              Dramatic
            </option>

          </select>

        </section>

        {/* Tone Mapping */}
        <section className="rounded-xl border border-border/60 bg-background/40 p-3 sm:p-4 space-y-3 m-1">
          <div className="flex items-center justify-between gap-3">
            <h2 className="text-sm font-medium">Tone Mapping</h2>
            <span className="text-xs text-muted-foreground">Render</span>
          </div>

          <div className="space-y-2">
            <label className="text-xs text-muted-foreground">Modelo</label>
            <select
              className="w-full h-10 rounded-md border border-border bg-background px-3 text-sm"
              value={params.toneMapping}
              onChange={(e) => pushParams({ ...params, toneMapping: e.target.value as ToneMappingKey })}
            >
              <option value="NoToneMapping">NoToneMapping</option>
              <option value="NeutralToneMapping">NeutralToneMapping</option>
              <option value="AgXToneMapping">AgXToneMapping</option>
              <option value="LinearToneMapping">LinearToneMapping</option>
              <option value="ReinhardToneMapping">ReinhardToneMapping</option>
              <option value="CineonToneMapping">CineonToneMapping</option>
              <option value="ACESFilmicToneMapping">ACESFilmicToneMapping</option>
            </select>
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <label className="text-xs text-muted-foreground">Exposure</label>
              <span className="text-xs tabular-nums text-foreground/80">
                {params.exposure.toFixed(2)}
              </span>
            </div>
            <input
              className="w-full"
              type="range"
              min={0}
              max={3}
              step={0.01}
              value={params.exposure}
              onChange={(e) => pushParams({ ...params, exposure: Number(e.target.value) })}
            />
          </div>

          <div className="space-y-2">
  <div className="flex items-center justify-between">
    <label className="text-xs text-muted-foreground">Camera FOV</label>
    <span className="text-xs tabular-nums text-foreground/80">
      {params.fov.toFixed(0)}°
    </span>
  </div>

  <input
    className="w-full"
    type="range"
    min={20}
    max={120}
    step={1}
    value={params.fov}
    onChange={(e) =>
      pushParams({ ...params, fov: Number(e.target.value) })
    }
  />
</div>
        </section>

        

        {/* Ambient */}
        <section className="rounded-xl border border-border/60 bg-background/40 p-3 sm:p-4 space-y-3 m-1">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-medium">Ambient</h2>

            <label className="flex items-center gap-2 text-xs text-muted-foreground select-none">
              <span>Ativo</span>
              <input
                type="checkbox"
                className="h-4 w-4"
                checked={params.ambientEnabled}
                onChange={(e) => pushParams({ ...params, ambientEnabled: e.target.checked })}
              />
            </label>
          </div>

          <div className={`space-y-3 ${!params.ambientEnabled ? "opacity-50" : ""}`}>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <label className="text-xs text-muted-foreground">Intensidade</label>
                <span className="text-xs tabular-nums text-foreground/80">
                  {params.ambientIntensity.toFixed(2)}
                </span>
              </div>
              <input
                className="w-full"
                type="range"
                min={0}
                max={3}
                step={0.01}
                value={params.ambientIntensity}
                disabled={!params.ambientEnabled}
                onChange={(e) => pushParams({ ...params, ambientIntensity: Number(e.target.value) })}
              />
            </div>

            <div className="flex items-center justify-between gap-3">
              <label className="text-xs text-muted-foreground">Cor</label>
              <div className="flex items-center gap-2">
                <span className="text-xs tabular-nums text-foreground/70">
                  {params.ambientColor.toUpperCase()}
                </span>
                <input
                  type="color"
                  value={params.ambientColor}
                  disabled={!params.ambientEnabled}
                  onChange={(e) => pushParams({ ...params, ambientColor: e.target.value })}
                  className="h-9 w-12 rounded-md border border-border bg-background p-1"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Environment */}
        <section className="rounded-xl border border-border/60 bg-background/40 p-3 sm:p-4 space-y-3 md:col-span-2 m-1">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-medium">Environment</h2>
            <span className="text-xs text-muted-foreground">Reflexos</span>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <label className="text-xs text-muted-foreground">EnvMap Intensity</label>
                <span className="text-xs tabular-nums text-foreground/80">
                  {params.envMapIntensity.toFixed(2)}
                </span>
              </div>
              <input
                className="w-full"
                type="range"
                min={0}
                max={1.5}
                step={0.01}
                value={params.envMapIntensity}
                onChange={(e) => pushParams({ ...params, envMapIntensity: Number(e.target.value) })}
              />
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <label className="text-xs text-muted-foreground">Rotação Y</label>
                <span className="text-xs tabular-nums text-foreground/80">
                  {params.envRotationY.toFixed(2)} rad
                </span>
              </div>
              <input
                className="w-full"
                type="range"
                min={-3.14159}
                max={3.14159}
                step={0.01}
                value={params.envRotationY}
                onChange={(e) => pushParams({ ...params, envRotationY: Number(e.target.value) })}
              />
            </div>
          </div>
        </section>

        {/* HDRI (apenas preset) */}
        <section className="rounded-xl border border-border/60 bg-background/40 p-3 sm:p-4 space-y-3 md:col-span-2 m-1">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-medium">HDRI</h2>

            {/**<label className="flex items-center gap-2 text-xs text-muted-foreground select-none">
              <span>Usar</span>
              <input
                type="checkbox"
                className="h-4 w-4"
                checked={params.hdriEnabled}
                onChange={(e) => pushParams({ ...params, hdriEnabled: e.target.checked })}
              />
            </label> */}
          </div>

          <div className={`space-y-2 ${!params.hdriEnabled ? "opacity-50" : ""}`}>
            <label className="text-xs text-muted-foreground">Preset</label>
            <select
              className="w-full h-10 rounded-md border border-border bg-background px-3 text-sm"
              value={params.hdrPresetUrl}
              disabled={!params.hdriEnabled}
              onChange={(e) => pushParams({ ...params, hdrPresetUrl: e.target.value })}
            >
              {hdrPresets.map((p) => (
                <option key={p.url} value={p.url}>
                  {p.label}
                </option>
              ))}
            </select>

            <p className="text-xs text-muted-foreground">
              HDRI define iluminação global e reflexos.
            </p>
          </div>
        </section>
      </div>

      {/* Footer */}
      <div className="pt-1 flex">
        <button
          className="w-full h-11 rounded-md bg-primary text-primary-foreground text-sm font-medium m-1"
          type="button"
          onClick={() => pushParams(DEFAULTS)}
        >
          Resetar
        </button>
        <button
          className="w-full h-11 rounded-md bg-primary text-primary-foreground text-sm font-medium m-1"
          type="button"
          onClick={() => saveLight(params)}
        >
          Salvar Iluminação
        </button>
      </div>
    </div>
  );
};

export default Light;